import type { Unit, UnitBody } from "@/models/units.model"
import type { AllISO2Codes } from "@/utils/lang/iso-map"
import { getCountryISO3 } from "@/utils/lang/iso-convert"
import { languageParser } from "../language.mapper"

export type TMedalCountConfig = {
  code: string
  quantity: number
}

export type UnitFormValues = {
  unitsNumber: number
  name: string
  longDescription: string
  shortDescription?: string
  order: number
  startDate: Date
  endDate: Date
  venue?: string
  hasMedals?: boolean
  medalCodes?: string[]
  medalQuantities?: Record<string, number>
  timegap?: string
  location: string
  staff?: {
    participantId: string
    function: {
      code: string
      description: Record<string, { long: string, short: string }>
      order: number
    }
    specificFunction: string
  }[]
}

export class UnitsFormMapper {
  /**
   * Transforma datos del formulario a la estructura UnitBody para enviar a la API
   */
  static toApiBody(formValues: UnitFormValues, phaseId: string, _existingUnit?: Unit, disciplineId?: string, language?: string): UnitBody {
    // Validamos que formValues no sea nulo o indefinido
    if (!formValues) {
      formValues = {} as UnitFormValues
    }
    const lang = language ?? "en"

    const iso3Code = getCountryISO3(lang as AllISO2Codes).toLowerCase()

    const body: UnitBody = {
      unitsNumber: formValues.unitsNumber || 0,
      name: formValues.name || "",
      description: {
        [iso3Code]: {
          long: formValues.longDescription || "",
          ...(formValues.shortDescription && { short: formValues.shortDescription }),
          lang: iso3Code,
        },
      },
      order: typeof formValues.order === "number" ? formValues.order : 1,
      hasMedals: formValues.hasMedals || false,
      medalCodes: formValues.medalCodes || [],
      medalQuantities: formValues.medalQuantities
        ? Object.entries(formValues.medalQuantities).map(([code, quantity]) => ({
            code,
            quantity,
          }))
        : [],
      metadata: {
        discipline: disciplineId || "",
        gender: "",
        sportEvent: "",
        category: "",
        phase: "",
        unit: formValues.unitsNumber ? formValues.unitsNumber.toString() : "0",
        phaseCode: phaseId || "",
      },
      dateInfo: {
        startDate: formValues.startDate instanceof Date ? formValues.startDate.toISOString() : new Date().toISOString(),
        endDate: formValues.endDate instanceof Date ? formValues.endDate.toISOString() : new Date().toISOString(),
      },
      location: formValues.location || "",
      venue: formValues.venue,
      unitTypeCode: "HATH",
    }

    // Solo incluir timegap si no es una edición (cuando _existingUnit no existe)
    if (!_existingUnit) {
      body.timegap = formValues.timegap || ""
    }

    return body
  }

  /**
   * Transforma datos de la API a la estructura del formulario
   */
  static toFormValues(unit: Unit): UnitFormValues {
    // Validamos que unit no sea nulo o indefinido
    if (!unit) {
      return {
        unitsNumber: 0,
        name: "",
        longDescription: "",
        shortDescription: undefined,
        order: 1,
        hasMedals: false,
        medalCodes: [],
        venue: undefined,
        startDate: new Date(),
        endDate: new Date(),
        location: "",
      }
    }

    // Validamos que los campos anidados existan
    const metadata = unit.metadata || {}
    const dateInfo = unit.dateInfo || {}

    // Procesamos los valores con validaciones
    let unitsNumber = 0
    try {
      unitsNumber = Number.parseInt(metadata.unit || "0", 10)
      if (Number.isNaN(unitsNumber))
        unitsNumber = 0
    }
    catch (e: unknown) {
      console.error("Error parsing unitsNumber:", e)
      unitsNumber = 0
    }

    console.log(unit)
    return {
      unitsNumber,
      name: unit.name || "",
      longDescription: languageParser(unit.description).long || "",
      shortDescription: languageParser(unit.description).short,
      order: typeof unit.order === "number" ? unit.order : 1,
      hasMedals: unit.hasMedals || false,
      medalCodes: (() => {
        // Extraer códigos únicos de medalQuantities para asegurar consistencia
        if (unit.medalQuantities && unit.medalQuantities.length > 0) {
          return [...new Set(unit.medalQuantities.map(medal => medal.code))]
        }
        return unit.medalCodes || []
      })(),
      medalQuantities: (() => {
        const medals = unit.medalQuantities
          ? unit.medalQuantities.reduce((acc, curr) => {
              acc[curr.code] = curr.quantity
              return acc
            }, {} as Record<string, number>)
          : {}
        console.log("Medal Quantities:", medals)
        return medals
      })(),
      timegap: unit.timegap || "",
      startDate: dateInfo.startDate ? new Date(dateInfo.startDate) : new Date(),
      endDate: dateInfo.endDate ? new Date(dateInfo.endDate) : new Date(),
      location: unit.location.id || "",
      venue: unit.venue
        ? unit.venue?.code
        : undefined,
      staff: Array.isArray(unit.staff)
        ? unit.staff.map(s => ({
            participantId: s.participantId || "",
            function: {
              code: s.function?.code || "",
              description: s.function?.description || {},
              order: typeof s.function?.order === "number" ? s.function.order : 0,
            },
            specificFunction: s.specificFunction || "",
          }))
        : undefined,
    }
  }
}
